import { type FC, useEffect } from 'react';

import { ButtonIconAction } from 'components/button/ButtonIconAction';
import { CustomButton } from 'components/header/style';
import { TagComponent } from 'components/tag/TagComponent';
import { SubTitle } from 'components/text/SubTitle';
import {
  Button,
  CalendarInput,
  TextInput,
  Tooltip,
  Typography,
  formatDate,
  neutral,
} from 'enable-ui';
import { setShowSaveAllShedule, showSaveAllScheduleSelector, useScheduleStore } from 'stores';

import { LessonType, type Lessons } from 'modules/dashboard/core/type';
import { Body } from 'modules/dashboard/styles/dashboard';

import { CustomCalendarInput } from '../styles/calendar';
import { Schedule } from '../styles/lesson';

import { ReactComponent as ReportIcon } from 'assets/icons/report-analytics.svg';

import { isBefore, isSameMinute } from 'date-fns';
import { isNull, isUndefined, uniqBy } from 'lodash';

import { COLOR, COLOR_CONFIG } from 'constants/color';

import { ExerciseContentType, LessonAssessmentType } from '../core/enum';

interface Props {
  item: Lessons;
  index: number;
  typeLesson: LessonType;
  totalLearner: number;
  handleChangeValue: (
    classLessonId: string,
    value: Date,
    startTime: string,
    endTime: string,
  ) => void;
  handleUpdateLesson: (
    classLessonId: string,
    classScheduleId: string,
    start: string,
    end: string,
  ) => void;
  goToLessonAssessmentDetailPage: (
    type: string,
    learnerId: string,
    classScheduleId: string,
    classLessonId: string,
  ) => void;
  onClickAction: (
    key: 'duplicate' | 'delete' | 'export',
    classLessonId: string,
    name?: string,
  ) => void;
}

export const LessonTableBodyItem: FC<Props> = ({
  index,
  item,
  typeLesson,
  handleChangeValue,
  handleUpdateLesson,
  goToLessonAssessmentDetailPage,
  totalLearner,
  onClickAction,
}) => {
  const showSaveAllShedule = useScheduleStore(showSaveAllScheduleSelector);

  const startDate = (item.start ? new Date(item.start) : '') as Date;

  const endDate = (item.end ? new Date(item.end) : '') as Date;

  const originStartDate = (item.originStart ? new Date(item.originStart) : '') as Date;

  const originEndDate = (item.originEnd ? new Date(item.originEnd) : '') as Date;

  const startTime = formatDate(startDate, 'HH:mm');

  const endTime = formatDate(endDate, 'HH:mm');

  const isDateBefore = isBefore(startDate, endDate);

  const isDirty =
    !isSameMinute(startDate, originStartDate) || !isSameMinute(endDate, originEndDate);

  const disabledTimePicker =
    (typeLesson === LessonType.PAST && isDateBefore) ||
    (!isUndefined(item?.action) && !isNull(item?.action));

  const disabledDatePicker =
    (!!startDate && !!endDate && disabledTimePicker) ||
    (!isUndefined(item?.action) && !isNull(item?.action));

  useEffect(() => {
    if (typeLesson !== LessonType.TO_BE_SCHEDULED) {
      return;
    }

    const value = uniqBy(
      [...showSaveAllShedule, { id: item.classLessonId ?? '', show: false }],
      'id',
    );

    const idx = value.findIndex((el) => el.id === item.classLessonId);

    if (idx !== -1) {
      const updatedItem = { ...value[idx], show: isDirty && isDateBefore };

      value[idx] = updatedItem;
    }

    setShowSaveAllShedule(value);

    return () => {
      setShowSaveAllShedule([]);
    };
  }, [isDirty, isDateBefore]);

  return (
    <Body
      $color={`${(index + 1) % 2 === 0 ? '#f9f9f9' : '#fff'}`}
      style={{
        // borderBottom: `1px solid ${neutral['neutral-3']}`,
        padding: '12px 0 0 0',
      }}
      key={index}
    >
      <div style={{ padding: '0 8px', width: 'calc(100% - 912px)', minWidth: 240 }}>
        <div>
          <Typography
            style={{
              textAlign: 'start',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: 240,
            }}
            variant="h5"
            title={item.name}
          >
            {item.name}
          </Typography>
          {item.lessonType === LessonAssessmentType.ASSESSMENTS ? (
            <Typography
              style={{
                textAlign: 'start',
                fontSize: '16px',
                lineHeight: '22px',
                color: '#808080',
              }}
            >
              {item.totalAssessments} tests
            </Typography>
          ) : (
            <SubTitle leftTitle={item.type?.name} rightTitle={`${item.totalExercises} exercises`} />
          )}
        </div>
      </div>
      <div
        style={{
          padding: '0 8px',
          width: '500px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <CustomCalendarInput>
          <CalendarInput
            placeholder="Select date"
            dropdownConfig={
              disabledDatePicker
                ? {
                    dropdownConfig: {
                      disabled: disabledDatePicker,
                    },
                  }
                : undefined
            }
            date={startDate}
            onChange={(value) => {
              handleChangeValue(item.classLessonId ?? '', value, startTime, endTime);
            }}
            disabled={disabledDatePicker}
            // minDate={new Date()}
          />
        </CustomCalendarInput>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Schedule $disabled={!startDate || !endDate || disabledTimePicker}>
            <TextInput
              type="time"
              value={startTime}
              onChange={(e) => {
                handleChangeValue(item.classLessonId ?? '', startDate, e.target.value, endTime);
              }}
              disabled={!startDate || disabledTimePicker}
              style={{ backgroundColor: 'transparent !important' }}
            />
            <span className="hyphen">-</span>
            <TextInput
              type="time"
              value={endTime}
              onChange={(e) => {
                handleChangeValue(item.classLessonId ?? '', startDate, startTime, e.target.value);
              }}
              disabled={!endDate || disabledTimePicker}
            />
          </Schedule>
          {!isDateBefore && !!startDate && !!endDate ? (
            <Typography color={COLOR_CONFIG.red}>Invalid duration</Typography>
          ) : null}
        </div>
      </div>
      <div style={{ padding: '0 8px', width: '110px' }}>
        <Typography style={{ textAlign: 'start' }} variant="h5">
          {`${item.totalCompleted ?? 0}/${totalLearner}`}
        </Typography>
      </div>
      <div style={{ padding: '0 8px', width: '122px' }}>
        <TagComponent
          text={
            item.lessonType === LessonAssessmentType.ASSESSMENTS
              ? 'Assessment'
              : item?.type?.key === ExerciseContentType.DIY
                ? 'DIY'
                : 'Supervised'
          }
          color={
            item.lessonType === LessonAssessmentType.ASSESSMENTS
              ? COLOR_CONFIG.magenta
              : item?.type?.key === ExerciseContentType.DIY
                ? COLOR_CONFIG.cyan
                : COLOR.purpel['purpel-6']
          }
        />
      </div>
      <div style={{ width: '100px' }}>
        {isDateBefore && isDirty ? (
          <CustomButton>
            <Button
              variant="outlined"
              style={{ padding: '0px 16px', height: 32 }}
              onClick={() =>
                handleUpdateLesson(
                  item.classLessonId ?? '',
                  item.classScheduleId ?? '',
                  item.start ?? '',
                  item.end ?? '',
                )
              }
            >
              Save
            </Button>
          </CustomButton>
        ) : (
          <></>
        )}
      </div>
      <div style={{ width: '32px' }}>
        <Tooltip
          content={
            <Typography
              variant="body-3"
              color={neutral['neutral-1']}
              style={{ fontWeight: '400', fontSize: 16, padding: '4px 0' }}
            >
              View details
            </Typography>
          }
          placement="top"
          arrow={false}
        >
          <ReportIcon
            onClick={() =>
              goToLessonAssessmentDetailPage(
                item.lessonType as string,
                item?.id,
                item?.classScheduleId ?? '',
                item?.classLessonId ?? '',
              )
            }
            style={{ cursor: 'pointer' }}
          />
        </Tooltip>
      </div>
      <div style={{ width: '36px' }}>
        <ButtonIconAction
          actions={[
            {
              label: 'Duplicate',
              onClick: () => onClickAction('duplicate', item?.classLessonId ?? ''),
            },
            {
              label: 'Delete',
              onClick: () => onClickAction('delete', item?.classLessonId ?? ''),
            },
            item.lessonType === LessonAssessmentType.ASSESSMENTS && item.classScheduleId
              ? {
                  label: 'Export',
                  onClick: () => onClickAction('export', item.classScheduleId ?? '', item.name),
                }
              : null,
          ]}
        />
      </div>
    </Body>
  );
};
